import { ChangeDetectionStrategy, Component, computed, effect, inject, input, Signal, signal } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { v4 as uuidv4 } from 'uuid';
import { NavigationEnd, Router } from '@angular/router';
import { environment } from '../../../../environments/environment';
import packageJson from '../../../../../../../package.json';
import { ApiMetadataRepository } from '../../../core/repository/api-metadata-repository';

@Component({
  selector: 'lib-side-bar',
  imports: [MatIcon],
  templateUrl: './side-bar.component.html',
  standalone: true,
  styleUrl: './side-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SideBarComponent {
  private router = inject(Router);
  private readonly apiMetadataRepository = inject(ApiMetadataRepository);

  sections = input.required<SideBarSection[]>();

  expanded = signal(true);
  currentURL = signal('');
  activeSectionPathSegment: Signal<string | undefined> = computed(() => {
    const currentUrlPathSegments = this.currentURL().split('/');
    if (currentUrlPathSegments.length > 1) {
      return currentUrlPathSegments[1];
    }
    return '';
  });

  private readonly apiVersion = signal<string>('');
  protected readonly versionString = computed(() => {
    return `${environment.environmentName}, ${packageJson.version}, API ${this.apiVersion()}`;
  });

  activeSectionItemPathSegment: Signal<string | undefined> = computed(() => {
    const currentUrlPathSegments = this.currentURL().split('/');
    if (currentUrlPathSegments.length > 2) {
      return currentUrlPathSegments[2];
    }
    return '';
  });

  defaultExpandActiveSectionEffect = effect(() => {
    this.sections().forEach(section => {
      if (section.pathSegment === this.activeSectionPathSegment()) {
        section.sectionExpanded.set(true);
      }
    });
  });

  constructor() {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.currentURL.set(event.url);
      }
    });

    this.apiMetadataRepository.getApiMetadata().subscribe(apiMetadata => {
      this.apiVersion.set(apiMetadata.applicationVersion ?? '');
    });
  }

  toggleExpanded() {
    this.expanded.update(e => !e);
  }

  toggleSectionExpanded(section: SideBarSection) {
    section.sectionExpanded.update(e => {
      if (section.items?.length ?? 0 > 0) {
        return !e;
      }
      return false;
    });
  }

  sectionClicked(section: SideBarSection) {
    if (section.items?.length ?? 0 > 0) {
      section.sectionExpanded.set(true);
    }
    this.router.navigate([`/${section.pathSegment}`]).then();
  }

  sectionItemClicked(section: SideBarSection, sectionItem: SideBarItem) {
    this.router.navigate([`/${section.pathSegment}/${sectionItem.pathSegment}`]).then();
  }

  // need to set this section as selected if we are looking at section record by id
  // Treat routes like /members/1234 as a section-record view when no section item matches the second path segment.
  // Let's hope we won't have a sub-section with the name 1234 :/
  protected isSectionRecord(section: SideBarSection): boolean {
    const activeSectionPathSegment = this.activeSectionPathSegment();
    const activeSectionItemPathSegment = this.activeSectionItemPathSegment();

    if (!activeSectionItemPathSegment || activeSectionPathSegment !== section.pathSegment) {
      return false;
    }

    return !section.items.some(item => item.pathSegment === activeSectionItemPathSegment);
  }
}

export class SideBarSection {
  public id: string = uuidv4();
  public pathSegment?: string;
  public title!: string;
  public icon!: string;
  public items: SideBarItem[];
  public sectionExpanded = signal(false);

  constructor(title: string, icon: string, pathSegment?: string, items?: SideBarItem[]) {
    this.title = title;
    this.icon = icon;
    this.items = items ?? [];
    this.pathSegment = pathSegment;
  }
}

export class SideBarItem {
  public id: string = uuidv4();
  public title!: string;
  public pathSegment?: string;

  constructor(title: string, pathSegment?: string) {
    this.title = title;
    this.pathSegment = pathSegment;
  }
}
